'use client';

import { c as _c } from "react/compiler-runtime";
import { Price } from '@7f-ui/components/price';
import { WishlistToggle } from '@feat/feat-wishlists/toggle';
import { CalendarBlank, Ticket } from '@phosphor-icons/react';
import { parseLocalDay } from '@utils/helpers';
import { formatDateWithWeekday } from '@utils/utils-format';
import { shopTestId } from '@utils/utils-shop-test-ids';
import { useAtomValue, useSetAtom } from 'jotai';
import { PreviewBadge } from 'src/app/_page-chrome/preview-badge';
import { useAppTicketSelection } from '../_hooks/use-app-ticket-selection';
import { selectedTicketDayAtom, ticketDialogAtom } from '../_lib/ticket-flow-atoms';
import { AppBookingBox } from './app-booking-box';
import { AppBookingQueryError } from './app-booking-query-error';
import { AppAnimatedExperienceCancelation, AppExperienceCancelation } from './app-experience-cancelation';
import { useExperienceFilterContext } from './app-experience-filter-provider';
import { AppExperienceRating } from './app-experience-rating';
import { AppStickyFooter } from './app-sticky-footer';
type AppExperienceTicketBookingBoxProps = {
  price: {
    price: number;
    category: string;
  } | null;
  location: string;
  onOpenReviews: () => void;
};
export function AppExperienceTicketBookingBox(t0) {
  const $ = _c(14);
  const {
    price,
    location,
    onOpenReviews
  } = t0;
  const {
    experience
  } = useExperienceFilterContext();
  const setActiveDialog = useSetAtom(ticketDialogAtom);
  const isAlwaysAvailable = experience.alwaysAvailable;
  let t1;
  if ($[0] !== setActiveDialog) {
    t1 = () => setActiveDialog("tickets");
    $[0] = setActiveDialog;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const openPurchaseDialog = t1;
  let t2;
  if ($[2] !== isAlwaysAvailable || $[3] !== location || $[4] !== onOpenReviews || $[5] !== openPurchaseDialog || $[6] !== price) {
    t2 = <div className="hidden sm:contents"><TicketBookingBoxDesktop price={price} location={location} isAlwaysAvailable={isAlwaysAvailable} onCtaClick={openPurchaseDialog} onOpenReviews={onOpenReviews} /></div>;
    $[2] = isAlwaysAvailable;
    $[3] = location;
    $[4] = onOpenReviews;
    $[5] = openPurchaseDialog;
    $[6] = price;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== openPurchaseDialog || $[9] !== price) {
    t3 = <div className="sm:hidden"><TicketBookingBoxMobile basePrice={price} onCtaClick={openPurchaseDialog} /></div>;
    $[8] = openPurchaseDialog;
    $[9] = price;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t2 || $[12] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
type TicketBookingBoxDesktopProps = {
  price: {
    price: number;
    category: string;
  } | null;
  location: string;
  isAlwaysAvailable: boolean;
  onCtaClick: () => void;
  onOpenReviews: () => void;
};
function TicketBookingBoxDesktop(t0) {
  const $ = _c(52);
  const {
    price,
    location,
    isAlwaysAvailable,
    onCtaClick,
    onOpenReviews
  } = t0;
  const {
    experience,
    dayPriceFailed,
    dayPriceIsPreliminary
  } = useExperienceFilterContext();
  const {
    ticketsLabel
  } = useAppTicketSelection();
  const setActiveDialog = useSetAtom(ticketDialogAtom);
  const selectedDay = useAtomValue(selectedTicketDayAtom);
  let t1;
  if ($[0] !== selectedDay) {
    t1 = selectedDay ? formatDateWithWeekday(parseLocalDay(selectedDay.date)) : null;
    $[0] = selectedDay;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedDayLabel = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = shopTestId("ExperienceBookingBox.container");
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== dayPriceFailed) {
    t3 = dayPriceFailed && <AppBookingBox.Title><TicketPriceError /></AppBookingBox.Title>;
    $[3] = dayPriceFailed;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== dayPriceFailed || $[6] !== dayPriceIsPreliminary || $[7] !== experience.currency || $[8] !== experience.hasMultiplePrices || $[9] !== price) {
    t4 = !!price && !dayPriceFailed && !dayPriceIsPreliminary && <AppBookingBox.Title><h3 className="font-heading3">{experience.hasMultiplePrices ? "ab " : null}<Price amount={price.price} currency={experience.currency} />{" "}<span className="font-body-small font-normal">{"pro "} {price.category}</span></h3></AppBookingBox.Title>;
    $[5] = dayPriceFailed;
    $[6] = dayPriceIsPreliminary;
    $[7] = experience.currency;
    $[8] = experience.hasMultiplePrices;
    $[9] = price;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== setActiveDialog) {
    t5 = () => setActiveDialog("tickets");
    $[11] = setActiveDialog;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = shopTestId("ExperienceBookingBox.pax");
    t7 = shopTestId("ExperienceBookingBox.pax-label");
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  let t8;
  if ($[15] !== ticketsLabel) {
    t8 = <span data-testid={t7}>{ticketsLabel}</span>;
    $[15] = ticketsLabel;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t5 || $[18] !== t8) {
    t9 = <AppBookingBox.Field label="Eintrittskarten"><AppBookingBox.Button Icon={Ticket} onClick={t5} data-testid={t6}>{t8}</AppBookingBox.Button></AppBookingBox.Field>;
    $[17] = t5;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== isAlwaysAvailable || $[21] !== selectedDayLabel || $[22] !== setActiveDialog) {
    t10 = !isAlwaysAvailable && <AppBookingBox.Field label="Datum"><AppBookingBox.Button Icon={CalendarBlank} onClick={() => setActiveDialog("date")} data-testid={shopTestId("ExperienceBookingBox.date")}><span data-testid={shopTestId("ExperienceBookingBox.date-label")}>{selectedDayLabel ?? "Datum w\xE4hlen"}</span></AppBookingBox.Button></AppBookingBox.Field>;
    $[20] = isAlwaysAvailable;
    $[21] = selectedDayLabel;
    $[22] = setActiveDialog;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = shopTestId("ExperienceBookingBox.cta");
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const t12 = dayPriceFailed || dayPriceIsPreliminary;
  let t13;
  if ($[25] !== onCtaClick || $[26] !== t12) {
    t13 = <AppBookingBox.CTA full={true} data-testid={t11} onClick={onCtaClick} disabled={t12}>Eintrittskarte wählen</AppBookingBox.CTA>;
    $[25] = onCtaClick;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const t14 = experience.images?.[0]?.url;
  let t15;
  if ($[28] !== experience.id || $[29] !== experience.publicId || $[30] !== experience.title || $[31] !== location || $[32] !== t14) {
    t15 = <WishlistToggle productId={experience.id} publicProductId={experience.publicId} productType="experience" productName={experience.title} locationName={location} image={t14} origin="pdp" />;
    $[28] = experience.id;
    $[29] = experience.publicId;
    $[30] = experience.title;
    $[31] = location;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t13 || $[35] !== t15) {
    t16 = <div className="flex items-center gap-4">{t13}{t15}</div>;
    $[34] = t13;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== experience.publicId || $[38] !== onOpenReviews) {
    t17 = <AppExperienceRating publicId={experience.publicId} className="justify-center" onClick={onOpenReviews} />;
    $[37] = experience.publicId;
    $[38] = onOpenReviews;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t10 || $[41] !== t16 || $[42] !== t17 || $[43] !== t3 || $[44] !== t4 || $[45] !== t9) {
    t18 = <AppBookingBox>{t3}{t4}{t9}{t10}{t16}{t17}</AppBookingBox>;
    $[40] = t10;
    $[41] = t16;
    $[42] = t17;
    $[43] = t3;
    $[44] = t4;
    $[45] = t9;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== experience.cancelationDeadline) {
    t19 = <div className="mt-6"><AppExperienceCancelation cancelationHours={experience.cancelationDeadline} variant="outlined" isTicket={true} /></div>;
    $[47] = experience.cancelationDeadline;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t18 || $[50] !== t19) {
    t20 = <div className="sticky-top-offset-10 sticky" data-testid={t2}>{t18}{t19}</div>;
    $[49] = t18;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  return t20;
}
function TicketPriceError() {
  const $ = _c(6);
  const {
    dayPriceIsFetching,
    refetchDayPrices
  } = useExperienceFilterContext();
  let t0;
  if ($[0] !== dayPriceIsFetching || $[1] !== refetchDayPrices) {
    t0 = () => {
      if (!dayPriceIsFetching) {
        refetchDayPrices({
          cancelRefetch: false
        });
      }
    };
    $[0] = dayPriceIsFetching;
    $[1] = refetchDayPrices;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== dayPriceIsFetching || $[4] !== t0) {
    t1 = <AppBookingQueryError error={{
      priceFailed: true,
      availabilityFailed: false,
      isRetrying: dayPriceIsFetching,
      onRetry: t0
    }} />;
    $[3] = dayPriceIsFetching;
    $[4] = t0;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
type TicketBookingBoxMobileProps = {
  basePrice: {
    price: number;
    category: string;
  } | null;
  onCtaClick: () => void;
};
function TicketBookingBoxMobile(t0) {
  const $ = _c(20);
  const {
    basePrice,
    onCtaClick
  } = t0;
  const {
    experience,
    dayPriceFailed,
    dayPriceIsPreliminary
  } = useExperienceFilterContext();
  let t1;
  if ($[0] !== experience.cancelationDeadline) {
    t1 = <AppAnimatedExperienceCancelation cancelationHours={experience.cancelationDeadline} isTicket={true} />;
    $[0] = experience.cancelationDeadline;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = dayPriceFailed || dayPriceIsPreliminary;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = shopTestId("ExperienceBookingBox.cta");
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== onCtaClick || $[4] !== t2) {
    t4 = {
      children: "Eintrittskarte w\xE4hlen",
      onClick: onCtaClick,
      disabled: t2,
      "data-testid": t3
    };
    $[3] = onCtaClick;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <PreviewBadge containerClassName="pointer-events-none absolute inset-x-0 bottom-full flex justify-center pb-6" />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== dayPriceFailed) {
    t6 = dayPriceFailed && <TicketPriceError />;
    $[7] = dayPriceFailed;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== basePrice || $[10] !== dayPriceFailed || $[11] !== dayPriceIsPreliminary || $[12] !== experience.currency || $[13] !== experience.hasMultiplePrices) {
    t7 = !!basePrice && !dayPriceFailed && !dayPriceIsPreliminary && <><div className="font-body-copy font-semibold">{experience.hasMultiplePrices ? "ab " : null}<Price amount={basePrice.price} currency={experience.currency} /></div><div>{`pro ${basePrice.category}`}</div></>;
    $[9] = basePrice;
    $[10] = dayPriceFailed;
    $[11] = dayPriceIsPreliminary;
    $[12] = experience.currency;
    $[13] = experience.hasMultiplePrices;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t1 || $[16] !== t4 || $[17] !== t6 || $[18] !== t7) {
    t8 = <AppStickyFooter popup={t1} buttonProps={t4}>{t5}{t6}{t7}</AppStickyFooter>;
    $[15] = t1;
    $[16] = t4;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}